import { useEffect, useState, useMemo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Typography
} from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import axiosConfig from 'src/Resources/axiosConfig';
import { SavingsCards } from './WeeklyReport';

Chart.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// ── Interfaces ───────────────────────────────────────────────

interface DailyEnergyData {
  date: string;
  pvProduction: number;
  loadConsumption: number;
  gridImport: number;
  gridExport: number;
  batteryCharged: number;
  batteryDischarged: number;
}

interface HourlyEnergyRecord {
  date: string;
  hour: number;
  pvKwh: number;
  loadKwh: number;
  gridImportKwh: number;
  batteryChargedKwh: number;
  batteryDischargedKwh: number;
  battSoC: number;
}

// ── Date Helpers ─────────────────────────────────────────────

function formatDateISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Returns the last 7 days ending yesterday.
 * Today is excluded because S3 aggregated file is not available until ~01:00 UTC the next day.
 */
function getLast7Days(): Date[] {
  const yesterday = new Date();
  yesterday.setHours(0, 0, 0, 0);
  yesterday.setDate(yesterday.getDate() - 1);

  const days: Date[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(yesterday);
    d.setDate(yesterday.getDate() - i);
    days.push(d);
  }
  return days;
}

// ── Main Component ───────────────────────────────────────────

export default function DailySection({
  installationId,
  onHasData,
  onPeriodChange
}: {
  installationId: number;
  onHasData?: (hasData: boolean) => void;
  onPeriodChange?: (date: string) => void;
}) {
  const intl = useIntl();
  const yesterday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - 1);
    return d;
  }, []);

  const [allRecords, setAllRecords] = useState<DailyEnergyData[]>([]);
  const [allHourlyRecords, setAllHourlyRecords] = useState<HourlyEnergyRecord[]>([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    const date = formatDateISO(yesterday);
    onPeriodChange?.(date);
    return date;
  });
  const [selectedDayRecord, setSelectedDayRecord] = useState<DailyEnergyData | null>(null);
  const [hourlyRecords, setHourlyRecords] = useState<HourlyEnergyRecord[]>([]);
  const [loadingWeek, setLoadingWeek] = useState(false);
  const [noData, setNoData] = useState(false);

  // Rolling 7-day window ending yesterday
  const weekDays = useMemo(() => getLast7Days(), []);

  // Fetch data for current week days
  useEffect(() => {
    if (weekDays.length === 0) return;
    const from = formatDateISO(weekDays[0]);
    const to = formatDateISO(weekDays[weekDays.length - 1]);
    setLoadingWeek(true);
    axiosConfig
      .get('/GetDailyDetailRecords', {
        params: { installationId, from, to }
      })
      .then((res) => {
        const daily = res.data?.dailyRecords?.records ?? [];
        const hourly = res.data?.hourlyRecords?.records ?? [];
        setAllRecords(Array.isArray(daily) ? daily : []);
        setAllHourlyRecords(Array.isArray(hourly) ? hourly : []);
        onHasData?.(Array.isArray(daily) && daily.length > 0);
      })
      .catch(() => {
        setAllRecords([]);
        setAllHourlyRecords([]);
        onHasData?.(false);
      })
      .finally(() => setLoadingWeek(false));
  }, [installationId, weekDays]);

  // When selected date changes, extract data from cache
  useEffect(() => {
    setNoData(false);
    setSelectedDayRecord(null);

    const cachedDay = allRecords.find((r) => r.date === selectedDate);
    const cachedHours = allHourlyRecords.filter((r) => r.date === selectedDate);

    if (cachedDay) {
      setSelectedDayRecord(cachedDay);
      setHourlyRecords(cachedHours);
    } else if (!loadingWeek) {
      setNoData(true);
      setHourlyRecords([]);
    }
  }, [installationId, selectedDate, allRecords, allHourlyRecords, loadingWeek]);

  const record = selectedDayRecord;

  const kpis = useMemo(() => computeKPIs(record), [record]);

  const handleStripSelect = (date: string) => {
    setSelectedDate(date);
    setNoData(false);
    onPeriodChange?.(date);
  };

  const dt = new Date(selectedDate + 'T00:00:00');
  const dateLabel = dt.toLocaleDateString(intl.locale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <>
      {/* Day Strip — last 7 days ending yesterday */}
      <DayStrip
        weekDays={weekDays}
        selectedDate={selectedDate}
        onSelect={handleStripSelect}
      />

      {/* Loading state */}
      {loadingWeek && !record && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 6
          }}
        >
          <CircularProgress size={40} style={{ color: '#ffc04d' }} />
        </Box>
      )}

      {/* No data state */}
      {!loadingWeek && noData && !record && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
          <Alert severity="warning">
            <FormattedMessage
              id="noReportData"
              defaultMessage="No report data found."
            />
          </Alert>
        </Box>
      )}

      {/* Day detail */}
      {record && (
        <>
          {/* Header */}
          <Paper
            sx={{ bgcolor: '#2c3e50', color: '#fff', p: 3, mb: 3, borderRadius: 2 }}
          >
            <Typography variant="h5" fontWeight="bold">
              <FormattedMessage
                id="dailyReportTitle"
                defaultMessage="Daily Energy Summary"
              />
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {dateLabel}
            </Typography>
          </Paper>

          {/* KPI Cards */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <SavingsCards
              intl={intl}
              energySaved={+kpis.energySaved.toFixed(1)}
              savingsCHF={kpis.savingsCHF}
              selfSufficiency={kpis.selfSufficiency}
              batteryEfficiency={kpis.batteryEfficiency}
            />
          </Paper>

          {/* Intraday Chart */}
          <IntradayChart
            hourlyData={hourlyRecords}
            loading={loadingWeek}
          />

          {/* Summary Table */}
          <DailySummaryTable record={record} />
        </>
      )}
    </>
  );
}

// ── KPI Computation ──────────────────────────────────────────

function computeKPIs(record: DailyEnergyData | null) {
  if (!record) {
    return { energySaved: 0, savingsCHF: 0, selfSufficiency: 0, batteryEfficiency: 0 };
  }
  const energySaved = Math.max(0, record.loadConsumption - record.gridImport);
  const savingsCHF = +(energySaved * 0.39).toFixed(2);
  const selfSufficiency =
    record.loadConsumption > 0
      ? Math.min(100, (1 - record.gridImport / record.loadConsumption) * 100)
      : 0;
  const batteryEfficiency =
    record.batteryCharged > 0
      ? Math.min(100, Math.floor((record.batteryDischarged / record.batteryCharged) * 100))
      : 0;
  return { energySaved, savingsCHF, selfSufficiency, batteryEfficiency };
}

// ── DayStrip ─────────────────────────────────────────────────

function DayStrip({
  weekDays,
  selectedDate,
  onSelect,
}: {
  weekDays: Date[];
  selectedDate: string;
  onSelect: (date: string) => void;
}) {
  const intl = useIntl();

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 1
        }}
      >
        {weekDays.map((day) => {
          const dateStr = formatDateISO(day);
          const isSelected = dateStr === selectedDate;
          return (
            <Paper
              key={dateStr}
              onClick={() => onSelect(dateStr)}
              elevation={isSelected ? 4 : 1}
              sx={{
                flex: '1 1 0',
                minWidth: 80,
                p: 1.5,
                textAlign: 'center',
                cursor: 'pointer',
                border: isSelected ? '2px solid #2980b9' : '2px solid transparent',
                bgcolor: isSelected ? '#e3f2fd' : '#fff',
                transition: 'all 0.15s',
                '&:hover': { bgcolor: isSelected ? '#e3f2fd' : '#f5f5f5' }
              }}
            >
              <Typography variant="caption" fontWeight="bold" sx={{ color: '#666' }}>
                {day.toLocaleDateString(intl.locale, { weekday: 'short' })}
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {day.getDate()}
              </Typography>
            </Paper>
          );
        })}
      </Box>
      <Typography variant="caption" sx={{ color: '#888' }}>
        <FormattedMessage
          id="currentWeekHint"
          defaultMessage="Last 7 days"
        />
      </Typography>
    </Box>
  );
}

// ── IntradayChart ────────────────────────────────────────────

const HOUR_LABELS = [
  ...Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`),
  '24:00'
];

function IntradayChart({
  hourlyData,
  loading
}: {
  hourlyData: HourlyEnergyRecord[];
  loading: boolean;
}) {
  const intl = useIntl();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, mb: 3 }}>
        <CircularProgress size={30} style={{ color: '#ffc04d' }} />
      </Box>
    );
  }

  if (hourlyData.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <FormattedMessage
          id="noHourlyData"
          defaultMessage="Hourly data not available for this day."
        />
      </Alert>
    );
  }

  const hourMap = new Map(hourlyData.map((h) => [h.hour, h]));

  const getHour = (i: number) => hourMap.get(i === 24 ? 23 : i);
  const pvData = HOUR_LABELS.map((_, i) => getHour(i)?.pvKwh ?? null);
  const loadData = HOUR_LABELS.map((_, i) => getHour(i)?.loadKwh ?? null);
  const batteryData = HOUR_LABELS.map((_, i) => {
    const h = getHour(i);
    return h ? h.batteryDischargedKwh - h.batteryChargedKwh : null;
  });
  const socData = HOUR_LABELS.map((_, i) => getHour(i)?.battSoC ?? null);

  const chartData = {
    labels: HOUR_LABELS,
    datasets: [
      {
        label: intl.formatMessage({ id: 'pvProduction', defaultMessage: 'PV Production' }),
        data: pvData,
        borderColor: '#f1c40f',
        backgroundColor: 'rgba(241,196,15,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        yAxisID: 'y'
      },
      {
        label: intl.formatMessage({ id: 'consumption', defaultMessage: 'Consumption' }),
        data: loadData,
        borderColor: '#e74c3c',
        backgroundColor: 'rgba(231,76,60,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        yAxisID: 'y'
      },
      {
        label: intl.formatMessage({ id: 'batteryPower', defaultMessage: 'Battery Power' }),
        data: batteryData,
        borderColor: '#3498db',
        backgroundColor: 'rgba(52,152,219,0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        yAxisID: 'y'
      },
      {
        label: intl.formatMessage({ id: 'batterySoCLabel', defaultMessage: 'Battery SoC' }),
        data: socData,
        borderColor: '#27ae60',
        borderDash: [6, 3],
        backgroundColor: 'transparent',
        tension: 0.3,
        pointRadius: 2,
        yAxisID: 'soc'
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { position: 'top' as const }
    },
    scales: {
      y: {
        position: 'left' as const,
        title: {
          display: true,
          text: intl.formatMessage({ id: 'powerKw', defaultMessage: 'Power (kW)' })
        }
      },
      soc: {
        position: 'right' as const,
        min: 0,
        max: 100,
        title: {
          display: true,
          text: intl.formatMessage({ id: 'socPercent', defaultMessage: 'SoC (%)' })
        },
        grid: { drawOnChartArea: false }
      }
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
        <FormattedMessage
          id="intradayChart"
          defaultMessage="Intraday Power Flow"
        />
      </Typography>
      <Box sx={{ height: 350 }}>
        <Line data={chartData} options={chartOptions} />
      </Box>
    </Paper>
  );
}

// ── DailySummaryTable ────────────────────────────────────────

function DailySummaryTable({ record }: { record: DailyEnergyData }) {
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: '#2c3e50' }}>
        <FormattedMessage id="dailySummary" defaultMessage="Daily Summary" />
      </Typography>
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& td, & th': { p: 1.5, borderBottom: '1px solid #eee' }
        }}
      >
        <thead>
          <tr style={{ background: '#f8f9fa' }}>
            <th style={{ textAlign: 'left' }}>
              <FormattedMessage id="metric" defaultMessage="Metric" />
            </th>
            <th style={{ textAlign: 'right' }}>
              <FormattedMessage id="total" defaultMessage="Total" />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><FormattedMessage id="pvProduction" defaultMessage="PV Production" /></td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
              {record.pvProduction.toFixed(1)} kWh
            </td>
          </tr>
          <tr>
            <td><FormattedMessage id="consumption" defaultMessage="Consumption" /></td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
              {record.loadConsumption.toFixed(1)} kWh
            </td>
          </tr>
          <tr>
            <td><FormattedMessage id="gridImport" defaultMessage="Grid Import" /></td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
              {record.gridImport.toFixed(1)} kWh
            </td>
          </tr>
          <tr>
            <td><FormattedMessage id="gridExport" defaultMessage="Grid Export" /></td>
            <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
              {record.gridExport.toFixed(1)} kWh
            </td>
          </tr>
          <tr style={{ background: '#f0f7ff' }}>
            <td>
              <strong>
                <FormattedMessage id="batteryCharged" defaultMessage="Battery Charged" />
              </strong>
            </td>
            <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#2980b9' }}>
              {record.batteryCharged.toFixed(1)} kWh
            </td>
          </tr>
          <tr style={{ background: '#f0f7ff' }}>
            <td>
              <strong>
                <FormattedMessage
                  id="batteryDischarged"
                  defaultMessage="Battery Discharged"
                />
              </strong>
            </td>
            <td style={{ textAlign: 'right', fontWeight: 'bold', color: '#e67e22' }}>
              {record.batteryDischarged.toFixed(1)} kWh
            </td>
          </tr>
        </tbody>
      </Box>
    </Paper>
  );
}
